import { useEffect, useRef, useState } from "react";

import {
    LoginChallenge, LoginMethod, loginMethodForChallenge, securityApi, securityErrorCode, securityErrorStatus, toLoginChallenge
} from "../../backend/security";
import { useSecondsRemaining } from "../../utils/countdown";
import { forgetPasskey, hasPasskeyHint, rememberPasskey } from "../../utils/passkey-hint";
import { getPasskeyAssertion, isPasskeySupported } from "../../utils/webauthn";

export interface LoginMfaChallengeOptions {
    challenge: LoginChallenge;
    /** Receives every server response: a finished sign-in or the next challenge. */
    onResult: (result: any) => Promise<void>;
    onCancel: () => void;
    /** Tells the user why the transaction ended; onCancel runs right after. */
    onAbandon: (message: string, description: string) => void;
    /**
     * Opens the passkey first when the step offers a password or a passkey. Defaults to whether
     * this browser has seen the account use one; re-authentication knows for certain.
     */
    preferPasskey?: boolean;
}

// Cognito challenges that ask the user to pick a method before proving it.
const CHOICE_CHALLENGES = ["SELECT_MFA_TYPE", "SELECT_CHALLENGE"];

// The transaction is short-lived, but a timer counting down from three minutes
// reads as pressure on a screen the user has barely arrived at. Only warn once
// the deadline is close enough to matter.
export const EXPIRY_WARNING_SECONDS = 60;

const CONNECTION_MESSAGE = "We couldn't reach Microtica. Check your connection and try again.";
const SERVER_MESSAGE = "Something went wrong on our side. Please try again in a moment.";

/**
 * True when the request never got an answer: the browser blocked it, the
 * connection dropped, or the gateway replied without CORS headers. Nothing was
 * verified, so the credential the user supplied is not in question.
 */
function isConnectionFailure(error: any): boolean {
    // The response interceptor spreads AxiosError into a plain object, which
    // keeps `code`/`message` but drops the prototype, so `instanceof` is out.
    return !error?.response
        && (error?.code === "ERR_NETWORK" || error?.code === "ECONNABORTED" || error?.message === "Network Error");
}

/** The server's own wording is safe to show. An axios message never is. */
function serverMessage(error: any): string | undefined {
    return error?.response?.data?.message;
}

export function formatRemaining(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${`${seconds % 60}`.padStart(2, "0")}`;
}

/** The sign-in challenge flow, shared by the classic and the new UI's views. */
export function useLoginMfaChallenge({ challenge, onResult, onCancel, onAbandon, ...options }: LoginMfaChallengeOptions) {
    // A method selection hands back a fresh transaction. It is tracked here rather than
    // lifted to the parent, because a parent re-key would remount this component
    // mid-request and lose the error a wrong code should show.
    const [active, setActive] = useState(challenge);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [selectedMethod, setSelectedMethod] = useState<LoginMethod>();
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(false);
    const secondsLeft = useSecondsRemaining(active.expiresAt);
    // One passkey ceremony per challenge. Set as soon as one starts, so the
    // auto-prompt below cannot race a ceremony the user began themselves.
    const passkeyAttempted = useRef(false);
    // A ceremony in flight. A second one on the same transaction makes the server refuse it,
    // which reads exactly like an account without a passkey.
    const passkeyRunning = useRef(false);
    const [noPasskey, setNoPasskey] = useState(false);
    // Read once, so the screen doesn't change under the user when the list is updated mid-flow
    const [preferPasskey] = useState(() => options.preferPasskey ?? hasPasskeyHint(challenge.username));

    // Only a new challenge resets the step. Effects can run twice for the same one (StrictMode
    // does it on mount), and clearing passkeyAttempted then would start a second ceremony.
    const shownChallenge = useRef(challenge);
    useEffect(() => {
        if (shownChallenge.current === challenge) {
            return;
        }
        shownChallenge.current = challenge;
        setActive(challenge);
        setSelectedMethod(undefined);
        setCode("");
        setPassword("");
        setError(undefined);
        passkeyAttempted.current = false;
    }, [challenge]);

    // The identifier step offers a passkey to every address, so an account without one only finds
    // out when it picks it. After that the choice is gone, and so is the choice screen when the
    // password is all that's left.
    const usable = (offered: LoginMethod[]) => noPasskey ? offered.filter(method => method.type !== "passkey") : offered;
    const methods = usable(active.availableFactors || []);
    const isChoice = CHOICE_CHALLENGES.includes(active.challengeName);

    // The first step offers exactly a password and a passkey. A menu of the two costs everyone a
    // click, so the likelier one opens (the passkey for an account that used one in this browser,
    // the password otherwise) and the other is a link. Taken from the challenge this step started
    // with, because choosing the passkey moves the server past the choice.
    const firstStep = CHOICE_CHALLENGES.includes(challenge.challengeName) ? usable(challenge.availableFactors || []) : [];
    const pairPasskey = firstStep.find(method => method.type === "passkey");
    const pairPassword = firstStep.find(method => method.type === "password");
    const pair = firstStep.length === 2 && pairPasskey && pairPassword ? { passkey: pairPasskey, password: pairPassword } : undefined;
    const pairDefault = pair && (preferPasskey && isPasskeySupported() ? pair.passkey : pair.password);

    const requiresChoice = isChoice && methods.length > 1 && !pair;
    const currentMethod = selectedMethod
        || pairDefault
        || (!requiresChoice && methods.length === 1 ? methods[0] : loginMethodForChallenge(active.challengeName));
    // The other half of the pair, offered as "Sign in with a passkey instead" or "Use your password instead"
    const alternative = pair && isPasskeySupported()
        ? (currentMethod?.type === "passkey" ? pair.password : pair.passkey)
        : undefined;

    const nextChallenge = (result: any) => {
        const next = toLoginChallenge(result, active.username);
        if (!next) {
            throw new Error("The sign-in response did not include the next step.");
        }
        setActive(next);
        return next;
    };

    /**
     * A fresh transaction for the same account. Cognito ends a session on a rejected password or
     * an unavailable method, and starting over is quicker done here than by retyping the email.
     */
    const restart = async () => {
        if (!active.username) {
            throw new Error("The sign-in has no account to restart for.");
        }
        return nextChallenge(await securityApi.startLogin(active.username));
    };

    /** Ends the transaction on the server side too, so the user must start over. */
    const abandon = (message: string, description: string) => {
        onAbandon(message, description);
        onCancel();
    };

    /** Keeps the user on this step. Used for failures the server lets them retry. */
    const handleRetryableError = (error: any, fallback: string) => {
        setCode("");

        if (isConnectionFailure(error)) {
            // The transaction is untouched, so the user can simply try again.
            setError(CONNECTION_MESSAGE);
            return;
        }

        const status = securityErrorStatus(error);
        const errorCode = securityErrorCode(error);

        if (status === 410 || errorCode === "TRANSACTION_EXPIRED") {
            abandon("Your sign-in request expired", "For your security this only stays open for a few minutes. Please start again.");
            return;
        }
        if (errorCode === "UNSUPPORTED_CHALLENGE" && requiresChoice) {
            setSelectedMethod(undefined);
            setError("This sign-in method isn't available right now. Choose another one.");
            return;
        }
        if (status && status >= 500) {
            setError(SERVER_MESSAGE);
            return;
        }
        setError(serverMessage(error) || fallback);
    };

    /** Asks the server to mail a code, then renders the challenge it returns. */
    const sendEmailCode = async () => {
        try {
            setLoading(true);
            setError(undefined);
            const selection = await securityApi.respondToLoginChallenge(active.transactionId, { factorId: "email" });
            if (selection?.done) {
                await onResult(selection);
                return;
            }
            nextChallenge(selection);
            setSelectedMethod(undefined);
        } catch (error: any) {
            handleRetryableError(error, "We couldn't send a code to your email. Try another method.");
        } finally {
            setLoading(false);
        }
    };

    const verifyTotp = async () => {
        if (code.length !== 6) {
            return;
        }
        try {
            setLoading(true);
            setError(undefined);
            let transactionId = active.transactionId;
            if (isChoice) {
                const selection = await securityApi.respondToLoginChallenge(transactionId, { factorId: "totp" });
                if (selection?.done) {
                    await onResult(selection);
                    return;
                }
                transactionId = nextChallenge(selection).transactionId;
            }
            // A wrong code keeps the transaction alive, so the user can try the next one.
            await onResult(await securityApi.respondToLoginChallenge(transactionId, { code }));
        } catch (error: any) {
            handleRetryableError(error, "The code was not accepted. Try the current code.");
        } finally {
            setLoading(false);
        }
    };

    const verifyPasskey = async () => {
        if (!isPasskeySupported()) {
            setError("This browser can't use passkeys. Try another browser, or sign in another way.");
            return;
        }
        if (passkeyRunning.current) {
            return;
        }
        passkeyAttempted.current = true;
        passkeyRunning.current = true;
        // Whether the failure came from choosing the passkey, before the browser was asked for one
        let selecting = false;
        try {
            setLoading(true);
            setError(undefined);
            let transactionId = active.transactionId;
            let publicKey = active.publicKey;
            if (!publicKey) {
                selecting = true;
                const selection = await securityApi.respondToLoginChallenge(transactionId, { factorId: "passkey" });
                selecting = false;
                if (selection?.done) {
                    await onResult(selection);
                    return;
                }
                const next = nextChallenge(selection);
                transactionId = next.transactionId;
                publicKey = next.publicKey;
            }
            if (!transactionId || !publicKey?.challenge) {
                throw new Error("The passkey request was missing from the sign-in challenge.");
            }
            const credential = await getPasskeyAssertion(publicKey);
            const result = await securityApi.respondToLoginChallenge(transactionId, { credential });
            // Next time this account gets its passkey prompt straight after the email
            rememberPasskey(active.username);
            await onResult(result);
        } catch (error: any) {
            if (error?.name === "NotAllowedError") {
                // The user dismissed the browser prompt, or the browser wanted a
                // click we did not have. Nothing was sent; the button is still there.
                return;
            }
            if (isConnectionFailure(error)) {
                setError(CONNECTION_MESSAGE);
                return;
            }
            if (securityErrorCode(error) === "RATE_LIMITED") {
                handleRetryableError(error, "Too many attempts. Try again later.");
                return;
            }
            const status = securityErrorStatus(error);
            if (status && status >= 500) {
                setError(SERVER_MESSAGE);
                return;
            }
            if (selecting && status && status < 500) {
                // Cognito refuses the passkey choice outright when the account has none
                // (or this browser remembered one that has since been removed)
                forgetPasskey(active.username);
                try {
                    await restart();
                    setNoPasskey(true);
                    setSelectedMethod(undefined);
                    setError(preferPasskey
                        ? "There's no passkey on this account anymore. Enter your password instead."
                        : "This account doesn't have a passkey yet. Sign in another way, then add one in Account security.");
                } catch {
                    abandon("This account doesn't have a passkey yet", "Sign in with your password or another method, then add a passkey in Account security.");
                }
                return;
            }
            if (status) {
                // Cognito treats a rejected assertion as final for this session.
                abandon("Couldn't verify passkey", serverMessage(error) || "Please start again.");
                return;
            }
            if (error?.name === "SecurityError") {
                // The passkey belongs to another address (its relying party), e.g. one added on
                // the deployed site and used from localhost. Retrying here can't help.
                setError("Passkeys added on another Microtica address don't work here. Sign in another way.");
                return;
            }
            setError("We couldn't use that passkey. Try again, or sign in another way.");
        } finally {
            passkeyRunning.current = false;
            setLoading(false);
        }
    };

    const verifyPassword = async () => {
        if (!password) {
            return;
        }
        try {
            setLoading(true);
            setError(undefined);
            await onResult(await securityApi.respondToLoginChallenge(active.transactionId, {
                password,
                ...(isChoice ? { factorId: "password" } : {})
            }));
        } catch (error: any) {
            if (isConnectionFailure(error)) {
                // Never blame the password for a request that never arrived.
                setError(CONNECTION_MESSAGE);
                return;
            }

            const status = securityErrorStatus(error);
            const errorCode = securityErrorCode(error);

            if (status === 410 || errorCode === "TRANSACTION_EXPIRED") {
                abandon("Your sign-in request expired", "For your security this only stays open for a few minutes. Please start again.");
                return;
            }
            if (status === 429 || errorCode === "RATE_LIMITED") {
                abandon("Too many attempts", "Wait a few minutes before trying again.");
                return;
            }
            if (status && status >= 500) {
                setError(SERVER_MESSAGE);
                return;
            }
            setPassword("");
            // A rejected password ends the Cognito session; a new one keeps the user on this step
            try {
                await restart();
                setError("That password isn't right. Try again or reset it. Signed up with GitHub or Google? Use that instead.");
            } catch {
                abandon("That password wasn't accepted", "Enter your email to start again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const passkeyIsOnlyStep = !requiresChoice && !selectedMethod && currentMethod?.type === "passkey";

    useEffect(() => {
        // Passkey is the only way forward here, so making the user click a button
        // that says what they already chose is pure ceremony. Browsers that insist
        // on a user gesture reject this with NotAllowedError and the button remains.
        if (!passkeyIsOnlyStep || passkeyAttempted.current || !isPasskeySupported()) {
            return;
        }
        void verifyPasskey();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passkeyIsOnlyStep]);

    /**
     * Swaps to the other half of the first step. The passkey prompt starts in the same click,
     * which browsers that want a user gesture for it need.
     */
    const switchTo = async (method: LoginMethod) => {
        setError(undefined);
        setPassword("");
        setCode("");
        setSelectedMethod(method);
        if (method.type === "passkey") {
            void verifyPasskey();
            return;
        }
        // Choosing the passkey already moved the server past the choice; a password needs a fresh one
        if (!isChoice) {
            try {
                setLoading(true);
                await restart();
            } catch {
                abandon("Your sign-in request expired", "Enter your email to start again.");
            } finally {
                setLoading(false);
            }
        }
    };

    /** Back to the list of methods, clearing anything typed for the last one */
    const chooseAnotherWay = () => {
        setSelectedMethod(undefined);
        setCode("");
        setPassword("");
        setError(undefined);
    };

    return {
        active, methods, requiresChoice, currentMethod, selectedMethod, setSelectedMethod,
        code, setCode, password, setPassword, error, setError, loading, secondsLeft,
        showExpiry: secondsLeft !== undefined && secondsLeft <= EXPIRY_WARNING_SECONDS,
        passkeySupported: isPasskeySupported(),
        sendEmailCode, verifyTotp, verifyPasskey, verifyPassword, chooseAnotherWay, alternative, switchTo,
    };
}
